import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { CSSProperties, ReactNode } from 'react'

export type ToastVariant = 'success' | 'warning' | 'info' | 'error'

export interface ToastAction {
  label: string
  onClick: () => void
}

interface ToastRecord {
  id: number
  message: string
  variant: ToastVariant
  action?: ToastAction
  /** false = entering/visible, true = exiting (animate out before removal). */
  exiting: boolean
}

export interface NotifyOptions {
  /** Override the default 3s auto-dismiss. Set to 0 to disable auto-dismiss
   *  entirely (caller is responsible for triggering removal — useful for
   *  toasts whose lifetime is tied to a server-enforced window, e.g. the
   *  60s approve-undo toast). */
  durationMs?: number
}

interface ToastContextValue {
  notify: (
    message: string,
    variant?: ToastVariant,
    action?: ToastAction,
    options?: NotifyOptions,
  ) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const VARIANT_BG: Record<ToastVariant, string> = {
  success: 'var(--success)',
  warning: 'var(--warning)',
  info: 'var(--steel-blue)',
  error: 'var(--danger)',
}

const AUTO_DISMISS_MS = 3000
const EXIT_MS = 100

interface ToastProviderProps {
  children: ReactNode
}

/**
 * Stacked top-right toaster. Wrap the app once, then call `useToast()`
 * inside any component to surface a transient message. Toasts auto-dismiss
 * after 3s; multiple toasts stack vertically with an 8px gap. Entry slides
 * in from the right + fades in over 150 ms; exit slides out + fades out
 * over 100 ms. Both gated by the global reduced-motion media query.
 */
export function ToastProvider({ children }: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<ReadonlyArray<ToastRecord>>([])
  const [mountedIds, setMountedIds] = useState<ReadonlyArray<number>>([])
  const counter = useRef<number>(0)
  const dismissTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const removeTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  const removeNow = useCallback((id: number): void => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    setMountedIds((prev) => prev.filter((mid) => mid !== id))
    const remove = removeTimers.current.get(id)
    if (remove) {
      clearTimeout(remove)
      removeTimers.current.delete(id)
    }
  }, [])

  const dismiss = useCallback(
    (id: number): void => {
      // Mark as exiting so the next paint runs the exit transition,
      // then unmount after EXIT_MS.
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)))
      const dismissHandle = dismissTimers.current.get(id)
      if (dismissHandle) {
        clearTimeout(dismissHandle)
        dismissTimers.current.delete(id)
      }
      const removeHandle = setTimeout(() => removeNow(id), EXIT_MS)
      removeTimers.current.set(id, removeHandle)
    },
    [removeNow],
  )

  const notify = useCallback(
    (
      message: string,
      variant: ToastVariant = 'info',
      action?: ToastAction,
      options?: NotifyOptions,
    ): void => {
      counter.current += 1
      const id = counter.current
      const next: ToastRecord = {
        id,
        message,
        variant,
        exiting: false,
        ...(action ? { action } : {}),
      }
      setToasts((prev) => [...prev, next])
      // Two-phase mount: render with translateX(20px), then flip mountedIds
      // on the next frame so the transition has somewhere to go.
      requestAnimationFrame(() => {
        setMountedIds((prev) => [...prev, id])
      })
      const ttl = options?.durationMs ?? AUTO_DISMISS_MS
      if (ttl > 0) {
        const handle = setTimeout(() => dismiss(id), ttl)
        dismissTimers.current.set(id, handle)
      }
    },
    [dismiss],
  )

  // Clear pending timers on unmount so tests don't leak.
  useEffect(() => {
    const dismissMap = dismissTimers.current
    const removeMap = removeTimers.current
    return () => {
      for (const handle of dismissMap.values()) clearTimeout(handle)
      for (const handle of removeMap.values()) clearTimeout(handle)
      dismissMap.clear()
      removeMap.clear()
    }
  }, [])

  const value = useMemo<ToastContextValue>(() => ({ notify }), [notify])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="region"
        aria-label="Notifications"
        aria-live="polite"
        style={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => {
          const mounted = mountedIds.includes(t.id) && !t.exiting
          const itemStyle: CSSProperties = {
            padding: '10px 18px',
            borderRadius: 10,
            background: VARIANT_BG[t.variant],
            color: 'var(--surface)',
            fontSize: 12,
            fontWeight: 600,
            boxShadow: 'var(--shadow-2)',
            pointerEvents: 'auto',
            transform: mounted ? 'translateX(0)' : 'translateX(20px)',
            opacity: mounted ? 1 : 0,
            transition: t.exiting
              ? 'transform var(--motion-duration-fast) var(--motion-ease-in),' +
                ' opacity var(--motion-duration-fast) var(--motion-ease-in)'
              : 'transform var(--motion-duration-base) var(--motion-ease-out),' +
                ' opacity var(--motion-duration-base) var(--motion-ease-out)',
          }
          return (
            <div
              key={t.id}
              data-testid="toast"
              data-variant={t.variant}
              data-exiting={t.exiting ? 'true' : 'false'}
              role="status"
              style={{ ...itemStyle, display: 'flex', alignItems: 'center', gap: 10 }}
            >
              <span style={{ flex: 1 }}>{t.message}</span>
              {t.action ? (
                <button
                  type="button"
                  data-testid="toast-action"
                  onClick={() => {
                    t.action!.onClick()
                    dismiss(t.id)
                  }}
                  style={{
                    flexShrink: 0,
                    background: 'rgba(255,255,255,0.25)',
                    border: 'none',
                    borderRadius: 6,
                    color: 'var(--surface)',
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '3px 8px',
                    cursor: 'pointer',
                    lineHeight: 1.4,
                  }}
                >
                  {t.action.label}
                </button>
              ) : null}
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be called inside <ToastProvider>')
  }
  return ctx
}

/**
 * Like `useToast`, but returns `null` instead of throwing when called outside
 * a `<ToastProvider>`. Useful for optional toast feedback in primitives that
 * may be rendered inside or outside the shell (e.g. `<ErrorState>`).
 */
export function useOptionalToast(): ToastContextValue | null {
  return useContext(ToastContext)
}
